import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Scale, BadgeCheck } from 'lucide-react';
import { apiRequest } from '../utils/api';
import LawyerInquiryForm from '../components/lawyer/LawyerInquiryForm';

export default function LawyerPublicProfilePage() {
  const { slug } = useParams();
  const [lawyer, setLawyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const data = await apiRequest(`lawyers/slug/${slug}`);
        if (!ignore) setLawyer(data);
      } catch (e) {
        if (!ignore) setError(e.message || 'Profile not found');
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    if (slug) load();
    return () => { ignore = true; };
  }, [slug]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : lawyer?.profile_url;

  const handleShare = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${lawyer?.display_name} — DocxsAi`, url: shareUrl });
      } catch {
        /* user cancelled */
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      alert('Profile link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] bg-[var(--color-bg)] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !lawyer) {
    return (
      <div className="min-h-[50vh] bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col items-center justify-center px-4">
        <p className="text-[var(--color-text-secondary)] mb-4">{error || 'Profile not found'}</p>
        <Link to="/find-a-lawyer" className="text-[var(--color-primary)] hover:text-[var(--color-primary)]">← Back to directory</Link>
      </div>
    );
  }

  const locationLabel = [lawyer.city, lawyer.state].filter(Boolean).join(', ')
    || lawyer.country
    || '';
  const headlineLine = [lawyer.headline, locationLabel].filter(Boolean).join(' | ');

  return (
    <div className="flex-1 bg-[var(--color-bg)] text-[var(--color-text)]">
      <Helmet>
        <title>{lawyer.display_name} | Find a Lawyer | DocxsAi</title>
        <meta name="description" content={lawyer.headline} />
        <meta property="og:title" content={`${lawyer.display_name} — ${lawyer.headline}`} />
        <meta property="og:description" content={lawyer.bio?.slice(0, 160)} />
        {lawyer.photo_url ? <meta property="og:image" content={lawyer.photo_url} /> : null}
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-28 sm:pt-32 pb-8 sm:pb-12">
        <Link to="/find-a-lawyer" className="inline-flex items-center gap-2 text-[var(--color-text-muted)] hover:text-[var(--color-text)] mb-8 text-sm">
          <ArrowLeft className="w-4 h-4" />
          Find a Lawyer
        </Link>

        <div className="overflow-hidden rounded-3xl border border-[var(--color-border)] elevated-card">
          {/* Cover — decorative only; no text overlaid on user uploads */}
          <div className="h-36 sm:h-44">
            {lawyer.cover_image_url ? (
              <img
                src={lawyer.cover_image_url}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full bg-gradient-to-r from-[var(--color-gradient-from)]/80 via-[var(--color-primary)]/60 to-[var(--color-gradient-to)]/80" />
            )}
          </div>

          {/* Identity — solid panel so name/details stay readable on any banner */}
          <div className="relative border-t border-[var(--color-border)] bg-[var(--color-surface)] px-6 sm:px-10 pb-10">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
              <div className="-mt-14 shrink-0 self-start sm:-mt-16">
                <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-2xl border-4 border-gray-900 bg-gradient-to-br from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] text-4xl font-bold shadow-xl sm:h-32 sm:w-32">
                  {lawyer.photo_url ? (
                    <img src={lawyer.photo_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    lawyer.display_name?.charAt(0)?.toUpperCase()
                  )}
                </div>
              </div>

              <div className="min-w-0 flex-1 sm:pt-3">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h1 className="text-2xl font-bold sm:text-3xl lg:text-4xl">{lawyer.display_name}</h1>
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-300">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    Verified listing
                  </span>
                </div>
                {lawyer.firm_name ? <p className="text-lg text-[var(--color-text-muted)]">{lawyer.firm_name}</p> : null}
                {headlineLine ? (
                  <p className="mt-2 text-lg text-[var(--color-primary)]">
                    {lawyer.headline ? (
                      <>
                        {lawyer.headline}
                        {locationLabel ? (
                          <>
                            <span className="text-[var(--color-text-muted)]"> | </span>
                            <span className="text-[var(--color-text-muted)]">{locationLabel}</span>
                          </>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-[var(--color-text-muted)]">{locationLabel}</span>
                    )}
                  </p>
                ) : null}
              </div>

              <button
                type="button"
                onClick={handleShare}
                className="shrink-0 self-start rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold hover:bg-[var(--color-primary-hover)] sm:mt-3"
              >
                Share profile
              </button>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {(lawyer.practice_areas || []).map((area) => (
                <span key={area} className="px-3 py-1 rounded-full bg-[var(--color-primary-soft)] text-[var(--color-primary)] text-sm border border-[var(--color-primary)]/20">
                  {area}
                </span>
              ))}
            </div>

            {lawyer.years_experience != null ? (
              <p className="text-[var(--color-text-muted)] text-sm mt-4">{lawyer.years_experience}+ years experience</p>
            ) : null}

            <div className="mt-8 prose prose-invert max-w-none">
              <h2 className="text-xl font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
                <Scale className="w-5 h-5 text-[var(--color-primary)]" />
                About
              </h2>
              <p className="text-[var(--color-text-secondary)] leading-relaxed whitespace-pre-wrap">{lawyer.bio}</p>
            </div>

            {(lawyer.languages || []).length > 0 ? (
              <div className="mt-8">
                <h3 className="text-sm font-semibold text-[var(--color-text-muted)] uppercase tracking-wide mb-2">Languages</h3>
                <p className="text-[var(--color-text-secondary)]">{lawyer.languages.join(', ')}</p>
              </div>
            ) : null}

            <LawyerInquiryForm slug={slug} />
          </div>
        </div>

        <p className="text-center text-gray-600 text-xs mt-8">
          Listed on DocxsAi Find a Lawyer · Profile reviewed before publication
        </p>
      </div>
    </div>
  );
}
